import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { useT } from "../settings/context";
import { Icon } from "../ui/Icon";
import { Modal } from "../ui/Modal";
import type { Coworker } from "./coworkers";
import { searchCoworkers } from "./coworkerSearch";

type Choice = {
  coworkers: ReadonlyArray<Coworker>;
  /** Already among the bearer's companions: « Déjà avec vous ». */
  companionIds: ReadonlySet<number>;
  /** How many more people the cap allows; `undefined` when there is no cap. */
  left: number | undefined;
  /** The coworker being attached, whose line spins; `null` when nothing is in flight. */
  choosing: number | null;
  /** The refusal of the last choice. Shown here: the page behind is hidden and inert. */
  error: string | null;
  onChoose: (who: Coworker) => void;
  onAddByHand: () => void;
};

/**
 * THE COWORKERS WINDOW: search the people of one's companies, choose who comes along.
 *
 * Always a window, whatever their number: one behaviour from one coworker to five
 * hundred. The step keeps a single button, where a list of a hundred cards pushed
 * « Ajouter une personne » out of sight.
 *
 * ⚠️ A CHOICE STILL REGISTERS ONE PERSON, AT ONCE. It attaches them and opens their
 * own steps (`routes/Companions.tsx`, `choose`): the window closes on success, and
 * stays open on a refusal, which it shows.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */
export function CoworkerPicker({
  open,
  onClose,
  ...choice
}: Choice & { open: boolean; onClose: () => void }) {
  const t = useT();
  const search = useRef<HTMLInputElement>(null);

  /**
   * ⚠️ THE SEARCH TAKES THE FOCUS, FROM HERE AND NOWHERE ELSE. `showModal()` focuses
   * the first focusable element, the close cross; React's `autoFocus` fires earlier,
   * while the dialog is still closed, and is lost. This effect belongs to the PARENT of
   * `Modal`, and React runs a child's effects first: the dialog is open when it runs.
   */
  useEffect(() => {
    if (open) search.current?.focus();
  }, [open]);

  return (
    <Modal
      open={open}
      title={t("COWORKERS_PICK_TITLE", "Choisir un collaborateur")}
      onClose={onClose}
      className="cwpick"
    >
      {/* Mounted only while open: every opening starts from an empty search. */}
      {open && <Choices {...choice} search={search} />}
    </Modal>
  );
}

function Choices({
  coworkers,
  companionIds,
  left,
  choosing,
  error,
  onChoose,
  onAddByHand,
  search,
}: Choice & { search: RefObject<HTMLInputElement | null> }) {
  const t = useT();
  const id = useId();
  const [query, setQuery] = useState("");
  const found = searchCoworkers(
    coworkers,
    query,
    (c) => c.registered || companionIds.has(c.id),
  );
  /* One registration at a time. `aria-disabled` rather than `disabled`: the button
     pressed keeps the focus, which a disabled one hands back to the page (bug #406). */
  const busy = choosing !== null;

  return (
    <>
      <div className="cwpick__q">
        <div className="f">
          <label htmlFor={id}>{t("SEARCH", "Rechercher")}</label>
          <input
            ref={search}
            id={id}
            type="search"
            value={query}
            autoComplete="off"
            placeholder={t("COWORKERS_SEARCH_PLACEHOLDER", "Un nom, une société")}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <p className="hint" role="status">
          {found.length !== coworkers.length
            ? t("N_OF_M", "$1 sur $2", [found.length, coworkers.length])
            : coworkers.length > 1
              ? t("N_COWORKERS_MANY", "$1 collaborateurs", [coworkers.length])
              : t("N_COWORKERS_ONE", "$1 collaborateur", [coworkers.length])}
        </p>
        {left !== undefined && (
          <p className="hint">
            {left > 1
              ? t("COMPANIONS_LEFT_MANY", "Vous pouvez encore inscrire $1 personnes.", [left])
              : t("COMPANIONS_LEFT_ONE", "Vous pouvez encore inscrire $1 personne.", [left])}
          </p>
        )}
        {error && (
          <p className="loginerr" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* The only part that scrolls: the search stays in reach above it. */}
      <div className="cwpick__l">
        {found.length > 0 ? (
          <ul className="opts">
            {found.map((c) => {
              const yours = companionIds.has(c.id);
              return (
                <li className="opts__row" key={c.id}>
                  {/* ⚠️ ONLY « CHOISIR » REGISTERS, NOT THE CARD. Scrolling a hundred
                      names with a thumb would otherwise register whoever it lands on. */}
                  <div className="opt">
                    <span className="opt__i" aria-hidden="true">
                      <Icon name="profil" />
                    </span>
                    <span className="opt__t">
                      <b>{c.name}</b>
                      {c.company && <small>{c.company}</small>}
                    </span>
                    <div className="opts__acts">
                      {yours ? (
                        <small className="hint">
                          {t("COWORKER_ALREADY_YOURS", "Déjà avec vous")}
                        </small>
                      ) : c.registered ? (
                        <small className="hint">
                          {t("COWORKER_ALREADY_REGISTERED", "Déjà inscrit")}
                        </small>
                      ) : (
                        <button
                          type="button"
                          className="lnk"
                          aria-disabled={busy || undefined}
                          aria-busy={choosing === c.id || undefined}
                          aria-label={t("CHOOSE_PERSON", "Choisir $1", [c.name])}
                          onClick={() => !busy && onChoose(c)}
                        >
                          {choosing === c.id && (
                            <span className="spin" aria-hidden="true" />
                          )}
                          {t("CHOOSE", "Choisir")}
                        </button>
                      )}
                      {choosing === c.id && (
                        <span className="a11y" role="status">
                          {t("IN_PROGRESS", "En cours")}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="empty">
            {query.trim()
              ? t("NO_COWORKER_MATCHES", "Personne ne correspond à « $1 ».", [
                  query.trim(),
                ])
              : t(
                  "COWORKERS_NONE",
                  "Aucun collaborateur de vos sociétés à proposer. Vous pouvez ajouter la personne à la main.",
                )}
          </p>
        )}
      </div>

      {/* Someone outside one's companies (a spouse, a client) is never in this list. */}
      <p className="cwpick__f">
        <span>{t("COWORKER_NOT_LISTED", "Vous ne trouvez pas la personne ?")}</span>
        <button
          type="button"
          className="lnk"
          aria-disabled={busy || undefined}
          onClick={() => !busy && onAddByHand()}
        >
          {t("ADD_BY_HAND", "L'ajouter à la main")}
        </button>
      </p>
    </>
  );
}
